import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { euro, fetchProduct, specList } from "../api/shop";
import { useCart } from "../context/CartContext";

const SPEC_ROWS = [
  ["storage", "Memoria interna"],
  ["ram", "RAM"],
  ["color", "Color"],
  ["network", "Red"],
  ["sim", "Tarjetas SIM"],
  ["display", "Pantalla"],
  ["displaySize", "Tamaño de pantalla"],
  ["resolution", "Resolución"],
  ["refreshRate", "Tasa de refresco"],
  ["chipset", "Chipset"],
  ["processor", "Procesador"],
  ["cores", "Núcleos"],
  ["os", "Sistema operativo"],
  ["battery", "Batería"],
  ["chargingSpeed", "Carga"],
  ["rearCamera", "Cámara trasera"],
  ["frontCamera", "Cámara frontal"],
  ["bodyMaterial", "Material"],
  ["dimensions", "Dimensiones"],
  ["weight", "Peso"],
  ["waterResistance", "Resistencia al agua"],
  ["warranty", "Garantía"],
];

const CONDITION_LABELS = { new: "Nuevo", refurbished: "Reacondicionado", used: "Usado" };

export default function ProductDetail() {
  const { sku } = useParams();
  const navigate = useNavigate();
  const { add, has } = useCart();

  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    setQuantity(1);
    setActiveImage(0);

    fetchProduct(sku)
      .then((res) => alive && setProduct(res))
      .catch((err) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));

    window.scrollTo({ top: 0 });
    return () => {
      alive = false;
    };
  }, [sku]);

  if (loading) {
    return (
      <div className="wrap pdp">
        <div className="hero__skeleton" style={{ height: 440 }} />
        <div>
          <div className="hero__skeleton" style={{ height: 40, marginBottom: 16 }} />
          <div className="hero__skeleton" style={{ height: 200 }} />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="wrap" style={{ padding: "60px 0" }}>
        <div className="state">
          <h3>No encontramos este móvil</h3>
          <p>{error || "Puede que ya no esté disponible en el catálogo."}</p>
          <Link to="/moviles" className="btn">
            Ver todos los móviles
          </Link>
        </div>
      </div>
    );
  }

  const specs = specList(product.specs);
  const outOfStock = product.stock !== null && product.stock !== undefined && product.stock <= 0;
  const inCart = has(product.sku);
  const gallery = product.images.length ? product.images : [product.image].filter(Boolean);
  const specRows = SPEC_ROWS.filter(([key]) => product.specs[key]);

  const addToCart = () => {
    add(product, quantity);
    navigate("/carrito");
  };

  return (
    <>
      <nav className="wrap crumbs" aria-label="Migas de pan">
        <Link to="/">Inicio</Link> › <Link to="/moviles">Móviles</Link> ›{" "}
        <Link to={`/moviles?brand=${encodeURIComponent(product.brand)}`}>{product.brand}</Link>
      </nav>

      <div className="wrap pdp">
        <div>
          <div className="pdp__gallery">
            <img src={gallery[activeImage]} alt={`${product.brand} ${product.title}`} />
          </div>
          {gallery.length > 1 && (
            <div className="pdp__thumbs">
              {gallery.map((src, i) => (
                <button
                  key={src}
                  className={i === activeImage ? "active" : ""}
                  onClick={() => setActiveImage(i)}
                  aria-label={`Ver foto ${i + 1}`}
                >
                  <img src={src} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="eyebrow">
            {product.brand} {product.condition !== "new" && `· ${CONDITION_LABELS[product.condition]}`}
          </p>
          <h1>{product.title}</h1>

          <div className="pdp__price">
            <span className="tag tag--lg">
              {euro(product.price * quantity)}
              <span className="tag__cur">€</span>
            </span>
            {product.originalPrice && (
              <span style={{ textDecoration: "line-through", color: "var(--stone)", marginLeft: 10 }}>
                {euro(product.originalPrice)} €
              </span>
            )}
            <span className="pdp__vat">
              IVA incluido{quantity > 1 && ` · ${euro(product.price)} € por unidad`}
            </span>
          </div>

          {specs.length > 0 && (
            <div className="specs" style={{ marginTop: 18 }}>
              {specs.map((spec) => (
                <span key={spec}>{spec}</span>
              ))}
            </div>
          )}

          <div className="buyrow">
            <div className="qty">
              <button onClick={() => setQuantity((n) => n - 1)} disabled={quantity <= 1} aria-label="Quitar una unidad">
                −
              </button>
              <span aria-live="polite">{quantity}</span>
              <button onClick={() => setQuantity((n) => n + 1)} disabled={quantity >= 10} aria-label="Añadir una unidad">
                +
              </button>
            </div>

            <button className="btn btn--lg" onClick={addToCart} disabled={outOfStock}>
              {outOfStock ? "Agotado" : "Añadir al carrito"}
            </button>

            {inCart && (
              <Link to="/carrito" className="linkish">
                Ya está en tu carrito
              </Link>
            )}
          </div>

          <div className="perks">
            <div>
              <b>Envío gratis</b>
              <span>Entrega en 24–72 h. Excluye Ceuta y Melilla.</span>
            </div>
            <div>
              <b>Garantía 3 años</b>
              <span>Terminal nuevo, libre y con garantía oficial en España.</span>
            </div>
            <div>
              <b>Devolución 14 días</b>
              <span>Si no es lo que esperabas, lo recogemos nosotros.</span>
            </div>
          </div>

          {specRows.length > 0 && (
            <>
              <h3 style={{ marginTop: 34 }}>Ficha técnica</h3>
              <dl className="sheet">
                <div className="sheet__row">
                  <dt>Marca</dt>
                  <dd>{product.brand || "—"}</dd>
                </div>
                <div className="sheet__row">
                  <dt>Modelo</dt>
                  <dd>{product.title}</dd>
                </div>
                {specRows.map(([key, label]) => (
                  <div className="sheet__row" key={key}>
                    <dt>{label}</dt>
                    <dd>{Array.isArray(product.specs[key]) ? product.specs[key].join(", ") : product.specs[key]}</dd>
                  </div>
                ))}
                {product.extraSpecs.map((row) => (
                  <div className="sheet__row" key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
                <div className="sheet__row">
                  <dt>Referencia</dt>
                  <dd>{product.sku}</dd>
                </div>
              </dl>
            </>
          )}

          {product.description && (
            <>
              <h3 style={{ marginTop: 30 }}>Descripción</h3>
              <p style={{ marginTop: 8, color: "var(--ink-80)", whiteSpace: "pre-line" }}>{product.description}</p>
            </>
          )}
        </div>
      </div>

      {product.related.length > 0 && (
        <section className="wrap section" style={{ paddingTop: 0 }}>
          <div className="section__head">
            <div>
              <p className="eyebrow">Misma marca</p>
              <h2>Otros {product.brand}</h2>
            </div>
          </div>
          <div className="grid">
            {product.related.map((item) => (
              <ProductCard key={item.sku} product={item} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
